import PropTypes from 'prop-types';

import {Box, Flex, Heading, Skeleton, Text} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';
import {HeroBannerBackgroundVideo} from '@amblique/king-living/app/utils/hero-banner-video';

/**
 * Hero Banner Block (CF-06)
 * Background image/video on top with content section below (title left, subtitle right)
 *
 * @typedef {import('@chakra-ui/styled-system').SystemStyleObject} SystemStyleObject
 * @typedef {"wrapper" | "mediaWrapper" | "backgroundMedia" | "contentSection" | "titleColumn" | "title" | "subtitleColumn" | "subtitle"} HeroBannerBlockCF06Parts
 * @typedef {Partial<Record<HeroBannerBlockCF06Parts, SystemStyleObject>>} HeroBannerBlockCF06Styles
 *
 * @param {Object} props - Component props
 * @param {Contentful.HeroBanner} props.component - HeroBanner component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {HeroBannerBlockCF06Styles} [props.styles] - Component styles
 * @returns {React.ReactElement}
 */
export const HeroBannerBlockCF06 = ({component, isComplete = true, isFetching = false, styles, ...props}) => {
  const {backgroundMedia, headingTitle, subtitle} = component;

  // Determine which background to use (Image has priority over Video)
  const backgroundImage = backgroundMedia?.__typename === 'Image' ? backgroundMedia : null;
  const backgroundVideo = backgroundMedia?.__typename === 'Video' ? backgroundMedia : null;

  return (
    <Box className="hero-banner" {...styles.wrapper}>
      {/* Background Media Section */}
      <Box className="hero-image-wrapper" {...styles.mediaWrapper}>
        {backgroundImage ? (
          <Component component={backgroundImage} fetchpriority="high" loading="eager" {...styles.backgroundMedia} />
        ) : backgroundVideo ? (
          <HeroBannerBackgroundVideo component={backgroundVideo} {...styles.backgroundMedia} />
        ) : (
          <Skeleton height="100%" width="100%" />
        )}
      </Box>

      {/* Content Section - Title Left, Subtitle Right - Only render if headingTitle or subtitle is present */}
      {headingTitle || subtitle ? (
        <Flex className="hero-content-wrapper" {...styles.contentSection}>
          {!isComplete && isFetching ? (
            <Skeleton height="120px" width="100%" />
          ) : (
            <>
              {/* Title Column - Left */}
              <Box {...styles.titleColumn}>
                {headingTitle && <Heading {...styles.title}>{headingTitle}</Heading>}
              </Box>

              {/* Subtitle Column - Right */}
              <Box {...styles.subtitleColumn}>
                {subtitle && <Text {...styles.subtitle}>{subtitle}</Text>}
              </Box>
            </>
          )}
        </Flex>
      ) : null}
    </Box>
  );
};

HeroBannerBlockCF06.displayName = 'HeroBannerBlockCF06';
HeroBannerBlockCF06.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  styles: PropTypes.object.isRequired,
};
